import classNames from 'classnames'
import { getLocationName, Location } from 'entities-next/common'
import parse from 'html-react-parser'
import ExpandMoreIcon from 'mdi-react/ExpandMoreIcon'
import React, { useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { ExtendableStyles } from 'utils-next/types'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  Theme,
  Typography,
} from '@mui/material'

import { SearchEngineCompany } from '../../SearchEngine.types'
import { SearchEngineCompanyDetail, SearchEngineCompanyDetailData } from './components'
import { alpha3ToAlpha2, formattedFoundedOn } from './helper'

export type SearchEngineCompanyRowProps = ExtendableStyles & {
  company: SearchEngineCompany
}

function ExtractionSource(props: any) {
  return (
    <div className={props.classes.sources}>
      {props.crunchbaseId ? (
        <Chip label="CrunchBase" size="small" style={{ backgroundColor: '#183444', color: '#fff' }} />
      ) : (
        ''
      )}
      {props.linkedinId ? (
        <Chip label="LinkedIn" size="small" style={{ backgroundColor: '#0a66c2', color: '#fff' }} />
      ) : (
        ''
      )}
      {props.pitchbookId ? (
        <Chip label="Pitchbook" size="small" style={{ backgroundColor: '#1D5080', color: '#fff' }} />
      ) : (
        ''
      )}
      {props.apolloId ? <Chip label="Apollo" size="small" style={{ backgroundColor: '#0a66c2', color: '#fff' }} /> : ''}
    </div>
  )
}

export function SearchEngineCompanyRow({ company, ...otherProps }: SearchEngineCompanyRowProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { classes } = useStyles()

  const companyDetails = useSearchEngineCompanyData(company)

  const { name, shortDescription, descriptionSubstring, lastFunding } = companyDetails

  let companyUrl = null

  try {
    if (companyDetails.companyUrl) {
      companyUrl = new URL(companyDetails.companyUrl)

      if (!companyUrl.protocol) {
        companyUrl.protocol = 'http'
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`Company ${name} does not have a valid companyUrl: ${companyDetails.companyUrl}`)
  }

  return (
    <Accordion onChange={(_, expanded) => setIsExpanded(expanded)} {...otherProps}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container spacing={3}>
          <Grid item md={6}>
            <Typography
              noWrap
              className={classNames(classes.nameContainer, {
                [classes.nameExpanded]: isExpanded,
              })}
            >
              <Box fontWeight="fontWeightMedium" className={classes.name}>
                {companyUrl ? (
                  <a
                    target="_blank"
                    href={companyUrl.toString()}
                    rel="noopener noreferrer"
                    style={{ color: '#1565c0', textDecoration: 'none' }}
                  >
                    {name}
                  </a>
                ) : (
                  <span>{name}</span>
                )}
              </Box>
              <ExtractionSource
                crunchbaseId={companyDetails.crunchbaseId}
                linkedinId={companyDetails.linkedinId}
                pitchbookId={companyDetails.pitchbookId}
                apolloId={companyDetails.apolloId}
                classes={classes}
              />
            </Typography>
          </Grid>
          <Grid item md={10}>
            {descriptionSubstring && !isExpanded && (
              <Typography color="textSecondary" variant="body2">
                {descriptionSubstring}
              </Typography>
            )}
          </Grid>
          <Grid item md={2}>
            {lastFunding && !isExpanded && (
              <Typography color="textSecondary" variant="body2">
                {lastFunding}
              </Typography>
            )}
          </Grid>
          <Grid item md={10}>
            {shortDescription && !isExpanded && (
              <Typography color="textSecondary" variant="body2">
                {shortDescription}
              </Typography>
            )}
          </Grid>
        </Grid>
      </AccordionSummary>
      <Divider />
      <AccordionDetails className={classes.details}>
        <SearchEngineCompanyDetail {...companyDetails} shortDescription={shortDescription} />
      </AccordionDetails>
    </Accordion>
  )
}

function useSearchEngineCompanyData({
  highlightedDescription,
  highlightedShortDescription,
  highlightedWebsiteContent,
  city,
  countryCode,
  lastFundingOn,
  lastFundingRound,
  ...company
}: SearchEngineCompany): SearchEngineCompanyListData & SearchEngineCompanyDetailData {
  const descriptionSubstring = highlightedDescription ? parse(highlightedDescription.substring(0, 500)) : undefined
  const description = highlightedDescription ? parse(highlightedDescription) : undefined
  const shortDescription =
    highlightedShortDescription && typeof highlightedShortDescription === 'string'
      ? parse(highlightedShortDescription)
      : undefined

  const escapedHighlightedWebsiteContent = highlightedWebsiteContent
    ? highlightedWebsiteContent
        .replace(/\\n/g, '\\n')
        .replace(/\\'/g, "\\'")
        .replace(/\\"/g, '\\"')
        .replace(/\\&/g, '\\&')
        .replace(/\\r/g, '\\r')
        .replace(/\\t/g, '\\t')
        .replace(/\\b/g, '\\b')
        .replace(/\\f/g, '\\f')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
        .replace('Ñempiezo', '<b>')
        .replace('Ñermino', '</b>')
    : ''

  const websiteContent = highlightedWebsiteContent
    ? parse(escapedHighlightedWebsiteContent.substring(0, 2000))
    : undefined

  const twoLettersCountryCode = countryCode && alpha3ToAlpha2[countryCode]

  const location: Partial<Location> = {
    city,
    countryCode: twoLettersCountryCode,
  }

  const parsedLocation = getLocationName(location)

  return {
    description,
    descriptionSubstring,
    shortDescription,
    websiteContent,
    parsedLocation,
    ...formattedFoundedOn(lastFundingRound, lastFundingOn),
    ...company,
  }
}

type SearchEngineCompanyListData = {
  name: string
  descriptionSubstring?: React.ReactNode
  lastFunding?: string
  shortDescription?: React.ReactNode
}

const useStyles = makeStyles()(({ spacing, palette, transitions }: Theme) => ({
  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing(1, 2),
  },
  table: {
    width: '100%',
    borderRadius: spacing(0.5),
    borderStyle: 'hidden',
    boxShadow: `0 0 0 1px ${palette.grey[200]}`,
  },
  doubleFieldWrapper: {
    display: 'grid',
    gridGap: spacing(),
    gridTemplateColumns: '1fr 3fr',
    width: '100%',
  },
  nameContainer: {
    transition: `all .25s ${transitions.easing.sharp}`,
    transformOrigin: 'left center',
  },
  name: {
    marginLeft: spacing(0.5),
  },
  sources: {
    display: 'flex',
    justifyContent: 'start',
    flexWrap: 'wrap',
    '& > *': {
      margin: spacing(0.5),
    },
  },
  nameExpanded: {
    transform: 'scale(1.5)',
    whiteSpace: 'normal',
  },
  details: {
    marginTop: spacing(2),
  },
  message: {
    marginBottom: spacing(2),
  },
}))
